import { useCallback, useEffect, useState } from 'react';

/**
 * A two-route history router.
 *
 * The app has exactly two pages: the wizard and the privacy policy. React Router
 * would be ~10x this file's weight for that, and the wizard's state lives in
 * React anyway. If a third route with real params ever appears, replace this —
 * it is deliberately small enough to throw away.
 */

export const ROUTES = {
  wizard: '/',
  privacy: '/politica-de-confidentialitate',
} as const;

export type Route = (typeof ROUTES)[keyof typeof ROUTES];

function currentPath(): Route {
  return window.location.pathname === ROUTES.privacy ? ROUTES.privacy : ROUTES.wizard;
}

export function useRoute(): [Route, (to: Route) => void] {
  const [route, setRoute] = useState<Route>(currentPath);

  useEffect(() => {
    const onPop = () => setRoute(currentPath());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = useCallback((to: Route) => {
    if (currentPath() === to) return;
    window.history.pushState({}, '', to);
    /*
     * Tell EVERY instance of this hook, not just the one that navigated.
     *
     * useState is per-caller, so `setRoute` here would only update the
     * component that called navigate(). The footer calls useRoute() and so
     * does App: clicking the footer link updated the footer's copy, App's
     * route never changed, and the URL moved while the page stayed put —
     * you had to reload to see the policy.
     *
     * pushState deliberately does not fire popstate, so we fire it ourselves
     * and let the existing listener below do the work. That keeps this hook
     * usable from anywhere instead of only from whoever owns the state.
     */
    window.dispatchEvent(new PopStateEvent('popstate'));
    window.scrollTo(0, 0);
  }, []);

  return [route, navigate];
}
